import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { useTheme } from '@/context/ThemeContext';
import {
  Buildings,
  Printer,
  Bell,
  Percent,
  Globe,
  WifiHigh,
  Palette,
  Check,
  ShoppingCart,
  Receipt,
  Clock,
  CheckCircle,
  XCircle,
  Monitor,
  CookingPot,
  ShieldCheck,
  Storefront,
  Truck,
  Sparkle,
  CaretLeft,
  ArrowRight,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { RoutingSettings } from '@/components/RoutingSettings';
import { IntegrationsPanel } from '@/components/IntegrationsPanel';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
import AIGrowthPage from '@/pages/AIGrowthPage';
const API_URL = __API_BASE__;

import {
  useAppSettingsData,
  SettingsSaveBar,
  Section,
  GeneralSettingsContent,
  ReceiptSettingsContent,
  NotificationsSettingsContent,
  POSSettingsContent,
  KDSSettingsContent,
  DeliverySettingsContent,
  AccessControlContent,
} from '@/components/AppSettingsPanel';

/* ------------ Public URL editor (outlet-level) ------------ */
const PublicUrlEditor = ({ outlet }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(outlet?.public_base_url || '');
  }, [outlet?.public_base_url]);

  if (!outlet) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/outlets/${outlet.id}/public-url`,
        { public_base_url: value || null },
        { withCredentials: true },
      );
      toast.success(value ? 'Public URL saved' : 'Public URL cleared');
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section icon={Globe} title="Public URL (for QR codes & receipts)" hint="Override the auto-detected host. Use this when your site is on a branded domain so QR codes don't leak the hosting provider's URL." testId="section-public-url">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Custom domain</Label>
          <Input
            placeholder="https://order.yourdomain.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="public-url-input"
          />
          <p className="text-[11px] text-[var(--theme-text-muted)]">
            Must start with <span className="font-mono">http://</span> or <span className="font-mono">https://</span>. Leave blank to use the browser's current domain.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[var(--theme-accent)] text-white"
          data-testid="public-url-save"
        >
          {saving ? 'Saving…' : 'Save Public URL'}
        </Button>
      </div>
    </Section>
  );
};

/* ------------ Theme Preview Card ------------ */
const ThemePreviewCard = ({ themeId, theme, isActive, onApply }) => {
  const { colors } = theme;
  return (
    <div
      className={`relative rounded-2xl border-2 p-5 transition-all duration-300 hover:shadow-lg cursor-pointer ${
        isActive ? 'border-[var(--theme-accent)] shadow-md' : 'border-[var(--theme-border)] hover:border-[var(--theme-accent)]/50'
      }`}
      style={{ backgroundColor: colors.surface }}
      onClick={() => onApply(themeId)}
      data-testid={`theme-card-${themeId}`}
    >
      {isActive && (
        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: colors.accent }}>
          <Check size={16} weight="bold" className="text-white" />
        </div>
      )}
      <h3 className="font-heading font-bold text-lg mb-1" style={{ color: colors.text }}>{theme.name}</h3>
      <p className="text-sm mb-4" style={{ color: colors.textMuted }}>{theme.description}</p>
      <div className="flex gap-2 mb-4">
        {['primary', 'accent', 'secondary', 'background', 'success'].map((k) => (
          <div key={k} className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full shadow-inner border border-white/20" style={{ backgroundColor: colors[k] }} />
            <span className="text-[10px]" style={{ color: colors.textMuted }}>{k[0].toUpperCase() + k.slice(1, 5)}</span>
          </div>
        ))}
      </div>
      <Button
        onClick={(e) => { e.stopPropagation(); onApply(themeId); }}
        className={`w-full h-11 rounded-xl font-medium transition-all duration-200 ${isActive ? 'opacity-50 cursor-default' : 'hover:scale-[1.02]'}`}
        style={{ backgroundColor: isActive ? colors.textMuted : colors.accent, color: 'white' }}
        disabled={isActive}
        data-testid={`apply-theme-${themeId}`}
      >
        {isActive ? 'Currently Active' : 'Apply Theme'}
      </Button>
    </div>
  );
};

/* ------------ Live Theme Preview Panel ------------ */
const LivePreviewPanel = ({ theme }) => {
  const { colors } = theme;
  return (
    <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
      <div className="px-4 py-3 border-b" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
        <h3 className="font-heading font-bold" style={{ color: colors.text }}>Live Preview – {theme.name}</h3>
      </div>
      <div className="p-4" style={{ backgroundColor: colors.background }}>
        <div className="flex gap-4">
          <div className="w-32 rounded-xl p-3 space-y-2" style={{ backgroundColor: colors.surface }}>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ backgroundColor: `${colors.accent}20` }}>
              <ShoppingCart size={14} style={{ color: colors.accent }} />
              <span className="text-xs font-medium" style={{ color: colors.accent }}>POS</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg">
              <Receipt size={14} style={{ color: colors.textMuted }} />
              <span className="text-xs" style={{ color: colors.textMuted }}>Orders</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg">
              <Clock size={14} style={{ color: colors.textMuted }} />
              <span className="text-xs" style={{ color: colors.textMuted }}>KDS</span>
            </div>
          </div>
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[['Orders', '24', colors.text], ['Sales', '$842', colors.accent], ['Active', '5', colors.success]].map(([l, v, c]) => (
                <div key={l} className="rounded-xl p-3" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <p className="text-[10px]" style={{ color: colors.textMuted }}>{l}</p>
                  <p className="text-lg font-bold" style={{ color: c }}>{v}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button className="flex-1 py-2 rounded-xl text-xs font-medium text-white" style={{ backgroundColor: colors.accent }}>New Order</button>
              <button className="flex-1 py-2 rounded-xl text-xs font-medium" style={{ backgroundColor: colors.secondary, color: colors.primary }}>View Tables</button>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 rounded-full text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: `${colors.secondary}50`, color: colors.primary }}><Clock size={10} /> New</span>
              <span className="px-2 py-1 rounded-full text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}><Clock size={10} /> Preparing</span>
              <span className="px-2 py-1 rounded-full text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: `${colors.success}20`, color: colors.success }}><CheckCircle size={10} /> Ready</span>
              <span className="px-2 py-1 rounded-full text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: `${colors.error}20`, color: colors.error }}><XCircle size={10} /> Cancelled</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------ Theme tab content ------------ */
const ThemePanelContent = () => {
  const { currentTheme, themes, applyTheme, theme: activeTheme } = useTheme();
  const [previewTheme, setPreviewTheme] = useState(null);
  return (
    <Card className="border border-[var(--theme-border)] bg-[var(--theme-surface)]">
      <CardHeader className="border-b border-[var(--theme-border)] bg-gradient-to-r from-[var(--theme-accent)]/5 to-transparent">
        <CardTitle className="font-heading text-xl flex items-center gap-3 text-[var(--theme-text)]">
          <div className="p-2 rounded-xl bg-[var(--theme-accent)]/10"><Palette size={24} className="text-[var(--theme-accent)]" /></div>
          Theme & Appearance
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <p className="text-[var(--theme-text-muted)] mb-6">Customize the look and feel. Choose a theme that matches your brand.</p>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <h4 className="font-medium text-[var(--theme-text)] mb-4">Available Themes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" onMouseLeave={() => setPreviewTheme(null)}>
              {Object.entries(themes).map(([themeId, themeData]) => (
                <div key={themeId} onMouseEnter={() => setPreviewTheme(themes[themeId])}>
                  <ThemePreviewCard themeId={themeId} theme={themeData} isActive={currentTheme === themeId} onApply={applyTheme} />
                </div>
              ))}
            </div>
          </div>
          <div className="xl:col-span-1">
            <h4 className="font-medium text-[var(--theme-text)] mb-4">Live Preview</h4>
            <LivePreviewPanel theme={previewTheme || activeTheme} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------ Outlet tab content ------------ */
const OutletPanelContent = ({ currentOutlet }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
    <Section icon={Storefront} title="Outlet Information" hint="Identity & contact" testId="section-outlet-info">
      <div className="space-y-3">
        <div className="space-y-1.5"><Label className="text-xs">Outlet Name</Label><Input value={currentOutlet?.name || ''} readOnly /></div>
        <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={currentOutlet?.address || ''} readOnly /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={currentOutlet?.phone || ''} readOnly /></div>
          <div className="space-y-1.5"><Label className="text-xs">Mode</Label><Input value={currentOutlet?.mode || ''} readOnly className="capitalize" /></div>
        </div>
      </div>
    </Section>
    <PublicUrlEditor outlet={currentOutlet} />
    <Section icon={Percent} title="Tax & Charges" hint="Outlet-level fiscal rates" testId="section-outlet-tax">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Tax Rate (%)</Label><Input type="number" value={currentOutlet?.tax_rate || 0} readOnly /></div>
          <div className="space-y-1.5"><Label className="text-xs">Service Charge (%)</Label><Input type="number" value={currentOutlet?.service_charge || 0} readOnly /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={currentOutlet?.currency || 'USD'} readOnly /></div>
        <p className="text-[11px] text-[var(--theme-text-muted)]">Edit these values from the General tab — they apply application-wide.</p>
      </div>
    </Section>
  </div>
);

/* ------------ Offline tab content ------------ */
const OfflinePanelContent = () => (
  <Section icon={WifiHigh} title="Offline Mode" hint="Continue operations without internet connection" testId="section-offline">
    <div className="space-y-3">
      <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--theme-background)]">
        <div>
          <p className="font-medium text-[var(--theme-text)]">Enable Offline Mode</p>
          <p className="text-sm text-[var(--theme-text-muted)]">Queue orders locally when network drops</p>
        </div>
        <Switch checked={true} data-testid="offline-enable" />
      </div>
      <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--theme-background)]">
        <div>
          <p className="font-medium text-[var(--theme-text)]">Auto-sync Orders</p>
          <p className="text-sm text-[var(--theme-text-muted)]">Sync queued orders when connection returns</p>
        </div>
        <Switch checked={true} data-testid="offline-autosync" />
      </div>
      <div className="p-4 rounded-xl bg-[var(--theme-accent)]/5 border border-[var(--theme-accent)]/20">
        <p className="text-sm text-[var(--theme-text-muted)]">
          Offline mode keeps the POS responsive even when internet drops. Orders are stored in IndexedDB
          and automatically flushed once a heartbeat is restored.
        </p>
      </div>
    </div>
  </Section>
);

/* ------------ Function Card (the launcher tile) ------------ */
const FunctionCard = ({ id, title, description, icon: Icon, gradient, accentBorder, accentTint, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(id)}
    data-testid={`settings-card-${id}`}
    className={`group relative w-full text-left rounded-2xl border-2 ${accentBorder} ${accentTint} overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--theme-background)] focus:ring-[var(--theme-accent)]/60`}
  >
    {/* Top accent stripe (always visible) */}
    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
    {/* Decorative gradient halo */}
    <div className={`absolute inset-x-0 -top-20 h-40 bg-gradient-to-br ${gradient} opacity-15 group-hover:opacity-30 blur-3xl transition-opacity`} />
    <div className="relative p-5">
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg ring-1 ring-white/30 mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <Icon size={24} weight="bold" className="text-white drop-shadow" />
      </div>
      <h3 className="font-heading font-bold text-base text-[var(--theme-text)] mb-1">{title}</h3>
      <p className="text-xs text-[var(--theme-text-muted)] leading-relaxed line-clamp-2 min-h-[2.4em]">{description}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--theme-text-muted)]">Configure</span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--theme-text)] opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
          Open <ArrowRight size={12} weight="bold" />
        </span>
      </div>
    </div>
  </button>
);

/* ------------ Function definitions ------------ */
const FUNCTIONS = [
  { id: 'general',       title: 'General',            description: 'Outlet identity, currency, tax & service charge defaults.',  icon: Buildings,   gradient: 'from-sky-500 to-blue-600',          accentBorder: 'border-sky-200/60 hover:border-sky-400 dark:border-sky-900/60 dark:hover:border-sky-500',           accentTint: 'bg-gradient-to-br from-sky-50/40 to-transparent dark:from-sky-950/30' },
  { id: 'outlet',        title: 'Outlet',             description: 'Outlet info, contact details, and public branded URL.',     icon: Storefront,  gradient: 'from-indigo-500 to-violet-600',     accentBorder: 'border-indigo-200/60 hover:border-indigo-400 dark:border-indigo-900/60 dark:hover:border-indigo-500', accentTint: 'bg-gradient-to-br from-indigo-50/40 to-transparent dark:from-indigo-950/30' },
  { id: 'theme',         title: 'Theme & Appearance', description: 'Switch themes and preview the look across the app.',         icon: Palette,     gradient: 'from-fuchsia-500 to-pink-600',      accentBorder: 'border-fuchsia-200/60 hover:border-fuchsia-400 dark:border-fuchsia-900/60 dark:hover:border-fuchsia-500', accentTint: 'bg-gradient-to-br from-fuchsia-50/40 to-transparent dark:from-fuchsia-950/30' },
  { id: 'pos',           title: 'POS',                description: 'Cart behaviour, payments, modifiers, and shortcuts.',        icon: Monitor,     gradient: 'from-amber-500 to-orange-600',      accentBorder: 'border-amber-200/60 hover:border-amber-400 dark:border-amber-900/60 dark:hover:border-amber-500',     accentTint: 'bg-gradient-to-br from-amber-50/40 to-transparent dark:from-amber-950/30' },
  { id: 'kds',           title: 'Kitchen Display',    description: 'KDS routing, color rules, and timing thresholds.',           icon: CookingPot,  gradient: 'from-rose-500 to-red-600',          accentBorder: 'border-rose-200/60 hover:border-rose-400 dark:border-rose-900/60 dark:hover:border-rose-500',         accentTint: 'bg-gradient-to-br from-rose-50/40 to-transparent dark:from-rose-950/30' },
  { id: 'delivery',      title: 'Delivery',           description: 'Delivery zones, fees, distance limits & live map.',          icon: Truck,       gradient: 'from-teal-500 to-cyan-600',         accentBorder: 'border-teal-200/60 hover:border-teal-400 dark:border-teal-900/60 dark:hover:border-teal-500',         accentTint: 'bg-gradient-to-br from-teal-50/40 to-transparent dark:from-teal-950/30' },
  { id: 'printers',      title: 'Printers & Routing', description: 'Receipt / kitchen printers and routing rules per station.',  icon: Printer,     gradient: 'from-slate-500 to-zinc-700',        accentBorder: 'border-slate-200/60 hover:border-slate-400 dark:border-slate-800 dark:hover:border-slate-500',         accentTint: 'bg-gradient-to-br from-slate-50/40 to-transparent dark:from-slate-900/40' },
  { id: 'receipt',       title: 'Receipt',            description: 'Header, footer, logo, layout & print/PDF preferences.',      icon: Receipt,     gradient: 'from-emerald-500 to-green-600',     accentBorder: 'border-emerald-200/60 hover:border-emerald-400 dark:border-emerald-900/60 dark:hover:border-emerald-500', accentTint: 'bg-gradient-to-br from-emerald-50/40 to-transparent dark:from-emerald-950/30' },
  { id: 'notifications', title: 'Notifications',      description: 'Order alerts, email/SMS toggles, sound preferences.',        icon: Bell,        gradient: 'from-yellow-500 to-amber-600',      accentBorder: 'border-yellow-200/60 hover:border-yellow-400 dark:border-yellow-900/60 dark:hover:border-yellow-500', accentTint: 'bg-gradient-to-br from-yellow-50/40 to-transparent dark:from-yellow-950/30' },
  { id: 'offline',       title: 'Offline Mode',       description: 'Queue orders locally and auto-sync when back online.',       icon: WifiHigh,    gradient: 'from-cyan-500 to-sky-600',          accentBorder: 'border-cyan-200/60 hover:border-cyan-400 dark:border-cyan-900/60 dark:hover:border-cyan-500',         accentTint: 'bg-gradient-to-br from-cyan-50/40 to-transparent dark:from-cyan-950/30' },
  { id: 'integrations',  title: 'Integrations',       description: 'Stripe, DoorDash, UberEats and third-party connectors.',     icon: Globe,       gradient: 'from-blue-500 to-indigo-600',       accentBorder: 'border-blue-200/60 hover:border-blue-400 dark:border-blue-900/60 dark:hover:border-blue-500',         accentTint: 'bg-gradient-to-br from-blue-50/40 to-transparent dark:from-blue-950/30' },
  { id: 'access',        title: 'Access Control',     description: 'Roles, permissions and feature toggles per user.',           icon: ShieldCheck, gradient: 'from-lime-500 to-emerald-600',      accentBorder: 'border-lime-200/60 hover:border-lime-400 dark:border-lime-900/60 dark:hover:border-lime-500',         accentTint: 'bg-gradient-to-br from-lime-50/40 to-transparent dark:from-lime-950/30' },
  { id: 'ai_growth',     title: 'AI Sales Growth Engine', description: 'Smart suggestions, upsells, inventory & profit insights.', icon: Sparkle,   gradient: 'from-violet-500 to-purple-600',     accentBorder: 'border-violet-200/60 hover:border-violet-400 dark:border-violet-900/60 dark:hover:border-violet-500', accentTint: 'bg-gradient-to-br from-violet-50/40 to-transparent dark:from-violet-950/30', isNew: true },
];

/* ------------ Settings Page (card grid launcher) ------------ */
export const SettingsPage = () => {
  const { currentOutlet } = useApp();
  const [activeFn, setActiveFn] = useState(null);
  const appSettings = useAppSettingsData(currentOutlet?.id);

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--theme-text-muted)]">Please select an outlet to view settings</p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeFn) {
      case 'general':       return <GeneralSettingsContent {...appSettings} />;
      case 'outlet':        return <OutletPanelContent currentOutlet={currentOutlet} />;
      case 'theme':         return <ThemePanelContent />;
      case 'pos':           return <POSSettingsContent {...appSettings} />;
      case 'kds':           return <KDSSettingsContent {...appSettings} />;
      case 'delivery':      return <DeliverySettingsContent {...appSettings} />;
      case 'printers':      return <RoutingSettings outletId={currentOutlet.id} />;
      case 'receipt':       return <ReceiptSettingsContent {...appSettings} />;
      case 'notifications': return <NotificationsSettingsContent {...appSettings} />;
      case 'offline':       return <OfflinePanelContent />;
      case 'integrations':  return <IntegrationsPanel outletId={currentOutlet.id} />;
      case 'access':        return <AccessControlContent {...appSettings} />;
      case 'ai_growth':     return <AIGrowthPage />;
      default:              return null;
    }
  };

  const activeFnDef = FUNCTIONS.find((f) => f.id === activeFn);

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {activeFn && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveFn(null)}
              data-testid="settings-back-button"
              className="gap-1"
            >
              <CaretLeft size={14} weight="bold" /> All Settings
            </Button>
          )}
          <div>
            <h1 className="font-heading text-3xl font-bold text-[var(--theme-text)]">
              {activeFnDef ? activeFnDef.title : 'Settings'}
            </h1>
            <p className="text-[var(--theme-text-muted)] mt-1">
              {activeFnDef ? activeFnDef.description : `${currentOutlet?.name} — pick a function to configure`}
            </p>
          </div>
        </div>
      </div>

      {/* Floating save bar (only when dirty and inside an editable panel) */}
      {activeFn && <SettingsSaveBar data={appSettings} />}

      {/* Card grid launcher */}
      {!activeFn && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2" data-testid="settings-card-grid">
          {FUNCTIONS.map((f) => (
            <div key={f.id} className="relative isolate">
              {f.isNew && (
                <span
                  className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-violet-500 to-purple-600 shadow-lg pointer-events-none"
                  data-testid={`settings-card-${f.id}-new-badge`}
                >
                  New
                </span>
              )}
              <FunctionCard {...f} onOpen={setActiveFn} />
            </div>
          ))}
        </div>
      )}

      {/* Detailed panel */}
      {activeFn && (
        <div data-testid={`settings-panel-${activeFn}`}>{renderContent()}</div>
      )}
    </div>
  );
};

export default SettingsPage;
